import { Button, Flex, Form, List, Modal, Spin } from 'antd';
import ActionHeader from '../../../../components/actionHeader';
import { StyledListItem } from '../../../../styles/listItem';
import { useEffect, useState, useMemo } from 'react';
import {
  fetchTemplateCauses,
  fetchTemplateIssues,
  selectIsFetchingTemplates,
  selectTemplateCurrentCauses,
  selectTemplateCurrentIssues,
  selectTemplateIssues,
  selectTemplateRejectedIssues,
  selectTemplateToBeReviewedIssues,
} from '../../../../slices/templates';
import { useParams } from 'react-router';
import { useAppDispatch } from '../../../../store';
import { useSelector } from 'react-redux';
import IssueCard from '../../../../components/issueCard';
import { ListContainer } from './styles';
import IssueForm from '../../../../components/issueForm';
import {
  fetchSolutionGrid,
  selectCurrentSolutionGridVersions,
  selectSolutionGrids,
} from '../../../../slices/solutionGrids';
import { initSolutionGrid } from '../../../../utils/dataMapper';
import { createSolutionGrid, deleteIssue } from '../../../../utils/httpRequest';
import { Issue, NewOrExisting } from '../../../../types';
import Tab from '../../../../components/tab';

const TemplateIssues = () => {
  const { id = '' } = useParams();
  const dispatch = useAppDispatch();
  const [openForm, setOpenForm] = useState(false);
  const [issueId, setIssueId] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const issues = useSelector(selectTemplateIssues);
  const causes = useSelector(selectTemplateCurrentCauses);

  const toBeReviewedIssues = useSelector(selectTemplateToBeReviewedIssues);
  const rejectedIssues = useSelector(selectTemplateRejectedIssues);
  const currentIssues = useSelector(selectTemplateCurrentIssues);

  const isFetching = useSelector(selectIsFetchingTemplates);
  const solutionGrids = useSelector(selectSolutionGrids);
  const currentSolutionGridVersions = useSelector(
    selectCurrentSolutionGridVersions
  );

  const linkedIssueIds = useMemo(() => {
    return new Set(solutionGrids.map((grid) => String(grid.issueID)));
  }, [solutionGrids]);

  const issueItem = issues?.find((i) => i.issueID === issueId);

  const handleSearch = (searchText?: string) => {
    if (!isFetching) {
      dispatch(
        fetchTemplateIssues({
          templateId: id,
          params: { search: searchText },
        })
      );
    }
  };

  const handleAddClick = () => {
    form.resetFields();
    setIssueId('');
    setOpenForm(true);
  };

  const handleEdit = (issueID: string) => {
    const item = issues?.find((i) => i.issueID === issueID);
    form.setFieldsValue(item);
    setOpenForm(true);
    setIssueId(issueID);
  };

  const handleDelete = async (issueID: string) => {
    try {
      setLoading(true);
      await deleteIssue(issueID);
      dispatch(fetchTemplateIssues({ templateId: id }));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (issue: Issue) => {
    setOpenForm(false);
    if (!solutionGrids.length) {
      const { newSolutionGrid } = initSolutionGrid({
        issues: [issue],
        causes: causes || [],
        templateID: id,
        existingSolutionGrids: solutionGrids || [],
        versionID: currentSolutionGridVersions?.versionID,
      });

      if (newSolutionGrid?.length) {
        createSolutionGrid(
          {
            newOrExisting: solutionGrids.length
              ? NewOrExisting.EXISTING
              : NewOrExisting.NEW,
            versionID: currentSolutionGridVersions?.versionID,
            solutionGridDtoList: newSolutionGrid,
          },
          String(id)
        );
      }
    }

    dispatch(fetchTemplateIssues({ templateId: id }));
  };

  useEffect(() => {
    if (!issues?.length) {
      dispatch(fetchTemplateIssues({ templateId: id }));
    }
    if (!causes?.length) {
      dispatch(fetchTemplateCauses({ templateId: id }));
    }
    if (!solutionGrids?.length) {
      dispatch(fetchSolutionGrid({ templateID: id }));
    }
  }, []);

  const tabs = [
    {
      id: 1,
      title: `Current (${currentIssues.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={currentIssues}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <IssueCard
                  issue={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                  showLinkedBadge
                  isLinked={linkedIssueIds.has(String(item.issueID))}
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 2,
      title: `To-Be-Reviewed (${toBeReviewedIssues.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={toBeReviewedIssues}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <IssueCard
                  issue={item}
                  showEdit
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                  showLinkedBadge
                  isLinked={linkedIssueIds.has(item.issueID)}
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
    {
      id: 3,
      title: `Rejected (${rejectedIssues.length})`,
      component: (
        <ListContainer>
          <List
            dataSource={rejectedIssues}
            loading={isFetching}
            renderItem={(item) => (
              <StyledListItem>
                <IssueCard
                  issue={item}
                  onEdit={handleEdit}
                  showDelete
                  onDelete={handleDelete}
                  showSuggestionStatus
                />
              </StyledListItem>
            )}
          ></List>
        </ListContainer>
      ),
    },
  ];

  return (
    <Flex vertical flex={1} gap={8} style={{ height: '100%' }}>
      <ActionHeader
        title="Issue"
        isSecondaryTitle
        showBorder={false}
        showSearch
        onSearch={handleSearch}
        endSlot={
          <>
            <Button type="primary" disabled ghost onClick={handleAddClick}>
              Batch Upload
            </Button>
            <Button type="primary" onClick={handleAddClick}>
              Add New Issue
            </Button>
          </>
        }
      />
      <Flex flex={1} style={{ overflowY: 'auto' }}>
        <Tab tabs={tabs} />
      </Flex>
      <Spin spinning={loading} fullscreen />
      <Modal
        title={issueId ? `Edit ${issueItem?.name}` : 'Add New Issue'}
        open={openForm}
        footer={null}
        width={800}
        destroyOnClose
        onClose={() => {
          setOpenForm(false);
        }}
        onCancel={() => setOpenForm(false)}
      >
        <Form form={form} layout="vertical">
          <IssueForm
            templateID={id}
            form={form}
            onCancel={() => setOpenForm(false)}
            onSubmit={handleSubmit}
            issue={issueItem}
          />
        </Form>
      </Modal>
    </Flex>
  );
};

export default TemplateIssues;
